import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Injector, inject, untracked } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService, SessionClearedError } from '@core/auth/services/auth.service/auth.service';
import { SKIP_AUTH_TOKEN } from '../http-context.tokens';
import { API_ENDPOINTS } from '@core/http/api-endpoints';

/**
 * Attaches the JWT bearer token and transparently refreshes the session on 401:
 * a failed request triggers one shared refresh call, then is retried with the
 * new token. If the refresh fails too, the user is logged out.
 *
 * Also stamps the no-store headers legacy put on every API call, so a GET is
 * never answered from the browser cache. Requests that set `Cache-Control`
 * themselves (image downloads) keep their own value.
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH_TOKEN)) {
    return next(withNoStore(req));
  }

  // Resolved via the injector (not a direct inject) so that requests fired
  // while other root services are still being constructed cannot create a
  // circular dependency through AuthService.
  const auth = inject(Injector).get(AuthService);

  // `untracked` z tego samego powodu co w LoaderService: interceptor bywa
  // uruchamiany synchronicznie ze środka `effect()`, a bez tego efekt uzależnia
  // się od sesji i każde jej odświeżenie odpala żądanie ponownie.
  const token = untracked(auth.session)?.token;
  const cacheable = withNoStore(req);
  const request = token ? withBearer(cacheable, token) : cacheable;

  return next(request).pipe(
    catchError((error: unknown) => {
      const canRefresh =
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        auth.session() !== null &&
        !isAuthEndpoint(req.url);

      if (!canRefresh) {
        return throwError(() => error);
      }

      return auth.refreshSession().pipe(
        switchMap((session) => next(withBearer(req, session.token))),
        catchError((refreshError: unknown) => {
          /*
           * Refresh failed — the session is gone for good. The login screen
           * says so, rather than looking like an unexplained logout.
           *
           * Chyba że odświeżenie zerwało się dlatego, że sesja skończyła się
           * w trakcie jego trwania (`SessionClearedError`). To była decyzja
           * użytkownika, więc „sesja wygasła" byłoby kłamstwem.
           *
           * Rozpoznajemy to po TYPIE błędu, a nie po tym, czy sesja jest teraz
           * pusta: użytkownik może się w międzyczasie zalogować z powrotem
           * (spóźnione odświeżenie potrafi wisieć sekundy), a wtedy warunek
           * „sesja istnieje" zerwałby mu dopiero co założoną, świeżą sesję.
           */
          if (!(refreshError instanceof SessionClearedError)) {
            auth.clearSession(true, 'auth.login.sessionExpired');
          }
          return throwError(() => error);
        }),
      );
    }),
  );
};

function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function withNoStore(req: HttpRequest<unknown>): HttpRequest<unknown> {
  if (req.headers.has('Cache-Control')) {
    return req;
  }
  return req.clone({
    setHeaders: { 'Cache-Control': 'no-store', Pragma: 'no-cache', Expires: '0' },
  });
}

function isAuthEndpoint(url: string): boolean {
  return url.includes(API_ENDPOINTS.auth.login) || url.includes(API_ENDPOINTS.auth.refresh);
}
